import React, { useState } from 'react';
import { Key, Plus, Eye, RefreshCw, Trash2 } from 'lucide-react';
import {
  useApiKeys,
  useRevokeApiKey,
  useRotateApiKey,
  useRevealApiKey,
} from '../../../hooks';
import CreateApiKeyModal from './CreateApiKeyModal';
import KeyCreatedModal from './KeyCreatedModal';
import RevealedKeyModal from './RevealedKeyModal';
import { ApiKeysTabSkeleton } from '../shared/Skeleton';

export default function ApiKeysTab() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [revealedKey, setRevealedKey] = useState(null);

  const { data, isLoading } = useApiKeys();
  const revokeApiKey = useRevokeApiKey();
  const rotateApiKey = useRotateApiKey();
  const revealApiKey = useRevealApiKey();

  const keys = data?.items || [];

  const handleRevoke = async (keyId) => {
    if (window.confirm('Are you sure you want to revoke this API key? This cannot be undone.')) {
      await revokeApiKey.mutateAsync(keyId);
    }
  };

  const handleRotate = async (keyId) => {
    if (window.confirm('Rotating will revoke the current key and create a new one. Continue?')) {
      const result = await rotateApiKey.mutateAsync(keyId);
      setNewKey(result);
      setShowKeyModal(true);
    }
  };

  const handleReveal = async (keyId) => {
    try {
      const result = await revealApiKey.mutateAsync(keyId);
      setRevealedKey(result);
    } catch (error) {
      alert(error.message || 'Could not reveal key.');
    }
  };

  const handleKeyCreated = (key) => {
    setNewKey(key);
    setShowCreateModal(false);
    setShowKeyModal(true);
  };

  function formatDate(dateStr) {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <div className="keys-container">
      <div className="keys-header">
        <div>
          <h2>API Keys</h2>
          <p>Manage your API keys for programmatic access</p>
        </div>
        <button className="workbench-btn workbench-btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={16} />
          Create API Key
        </button>
      </div>

      {isLoading ? (
        <ApiKeysTabSkeleton />
      ) : keys.length === 0 ? (
        <div className="workbench-empty">
          <Key size={48} />
          <h3>No API keys created yet</h3>
          <p>Create an API key to get started with the API</p>
        </div>
      ) : (
        <div className="workbench-card">
          <table className="workbench-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Last Used</th>
                <th>Created</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id}>
                  <td className="key-name">{key.name}</td>
                  <td>
                    <code className="workbench-code-inline">{key.key_prefix}...</code>
                  </td>
                  <td>{formatDate(key.last_used_at)}</td>
                  <td>{formatDate(key.created_at)}</td>
                  <td>
                    <span className={`workbench-badge ${key.is_active ? 'workbench-badge-success' : 'workbench-badge-error'}`}>
                      {key.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td>
                    <div className="action-btns">
                      <button
                        className="action-btn"
                        title="View key"
                        onClick={() => handleReveal(key.id)}
                        disabled={!key.is_active || revealApiKey.isPending}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        className="action-btn"
                        title="Rotate key"
                        onClick={() => handleRotate(key.id)}
                        disabled={!key.is_active || rotateApiKey.isPending}
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        className="action-btn danger"
                        title="Revoke key"
                        onClick={() => handleRevoke(key.id)}
                        disabled={!key.is_active || revokeApiKey.isPending}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <CreateApiKeyModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleKeyCreated}
        />
      )}

      {showKeyModal && newKey && (
        <KeyCreatedModal
          keyData={newKey}
          onClose={() => { setShowKeyModal(false); setNewKey(null); }}
        />
      )}

      {revealedKey && (
        <RevealedKeyModal
          keyData={revealedKey}
          onClose={() => setRevealedKey(null)}
        />
      )}
    </div>
  );
}
